import { map, tileLayer, Marker, Icon, LayerGroup, Circle, latLng, type Map as LeafletMap } from "leaflet";
import $ from "jquery";

// ---------------------------------------------------------------------------
// Tipi
// ---------------------------------------------------------------------------

interface OverpassElement {
    lat: number;
    lon: number;
    tags: {
        amenity: string;
        name?: string;
    };
}

interface OverpassResponse {
    elements: OverpassElement[];
}

// ---------------------------------------------------------------------------
// Costanti
// ---------------------------------------------------------------------------

const AROUND_POI_METERS = 600;

const AMENITY_TRANSLATIONS: Record<string, string> = {
    school:           "Scuola",
    hospital:         "Ospedale",
    cinema:           "Cinema",
    restaurant:       "Ristorante",
    pharmacy:         "Farmacia",
    parking:          "Parcheggio",
    university:       "Università",
    park:             "Parco",
    bank:             "Banca",
    supermarket:      "Supermercato",
    bus_station:      "Stazione bus",
    charging_station: "Stazione ricarica elettrica",
};

function makeIcon(name: string): Icon {
    return new Icon({
        iconUrl:     `../assets/images/icon/${name}.svg`,
        iconSize:    [32, 32],
        iconAnchor:  [16, 32],
        popupAnchor: [0, -32],
    });
}

// ---------------------------------------------------------------------------
// Export principale
// ---------------------------------------------------------------------------

/**
 * Inizializza la mappa servizi (#mapserv) con i POI OpenStreetMap intorno
 * alle coordinate del bene in vendita.
 * È un no-op se gli elementi #Latitudine, #Longitudine o #mapserv non sono
 * presenti nel DOM.
 */
export async function InitServiziMappa(): Promise<void> {
    const latRaw = $("#Latitudine").val() as string | undefined;
    const lngRaw = $("#Longitudine").val() as string | undefined;

    if (!$("#mapserv").length || !latRaw || !lngRaw) {
        return;
    }

    const lat = parseFloat(latRaw.replace(",", "."));
    const lng = parseFloat(lngRaw.replace(",", "."));

    // ---- Mappa ----
    const mapserv: LeafletMap = map("mapserv", {
        center:          [lat, lng],
        zoom:            15,
        zoomControl:     false,
        dragging:        false,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        minZoom:         11,
        maxZoom:         20,
        touchZoom:       false,
    });

    tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "Map data © OpenStreetMap contributors",
    }).addTo(mapserv);

    new Marker([lat, lng]).addTo(mapserv);

    // ---- Icone ----
    const schoolIcon   = makeIcon("education");
    const bankIcon     = makeIcon("bank");
    const restaurantIcon = makeIcon("restaurant");
    const pharmacyIcon = makeIcon("pharmacy");
    const hospitalIcon = makeIcon("hospital");
    const cinemaIcon   = makeIcon("cinema");
    const busIcon      = makeIcon("bus");
    const chargeIcon   = makeIcon("charge");
    const parkingIcon  = makeIcon("parking");

    // ---- Layer ----
    const schoolLayer          = new LayerGroup();
    const bankLayer            = new LayerGroup();
    const hospitalLayer        = new LayerGroup();
    const cinemaLayer          = new LayerGroup();
    const restaurantLayer      = new LayerGroup();
    const pharmacyLayer        = new LayerGroup();
    const busStationLayer      = new LayerGroup();
    const chargingStationLayer = new LayerGroup();
    const parkingLayer         = new LayerGroup();
    const universityLayer      = new LayerGroup();

    const layers: Record<string, LayerGroup> = {
        cinema:           cinemaLayer,
        school:           schoolLayer,
        bank:             bankLayer,
        hospital:         hospitalLayer,
        restaurant:       restaurantLayer,
        pharmacy:         pharmacyLayer,
        bus_station:      busStationLayer,
        charging_station: chargingStationLayer,
        parking:          parkingLayer,
        university:       universityLayer,
    };

    // ---- Contatori POI ----
    const amenityCount: Record<string, number> = {};

    // ---- Fetch POI ----
    await getPOI(lat, lng);

    // Mostra cerchio solo se ci sono POI
    if (Object.keys(amenityCount).length > 0) {
        new Circle([lat, lng], {
            color:       "blue",
            fillColor:   "#1C93FF",
            fillOpacity: 0.3,
            radius:      AROUND_POI_METERS,
        }).addTo(mapserv);
    }

    // ---- Funzioni interne ----

    async function getPOI(latP: number, lngP: number): Promise<void> {
        const query = `
        [out:json];
        (
            node["amenity"="school"](around:${AROUND_POI_METERS}, ${latP}, ${lngP});
            node["amenity"="university"](around:${AROUND_POI_METERS}, ${latP}, ${lngP});
            node["amenity"="bank"](around:${AROUND_POI_METERS}, ${latP}, ${lngP});
            node["amenity"="hospital"](around:${AROUND_POI_METERS}, ${latP}, ${lngP});
            node["amenity"="cinema"](around:${AROUND_POI_METERS}, ${latP}, ${lngP});
            node["amenity"="restaurant"](around:${AROUND_POI_METERS}, ${latP}, ${lngP});
            node["amenity"="pharmacy"](around:${AROUND_POI_METERS}, ${latP}, ${lngP});
            node["amenity"="bus_station"](around:${AROUND_POI_METERS}, ${latP}, ${lngP});
            node["amenity"="charging_station"](around:${AROUND_POI_METERS}, ${latP}, ${lngP});
            node["amenity"="parking"](around:${AROUND_POI_METERS}, ${latP}, ${lngP});
        );
        out body;
        `;

        const url = "https://overpass-api.de/api/interpreter?data=" + encodeURIComponent(query);
        const controller = new AbortController();
        const timeoutId  = setTimeout(() => controller.abort(), 8000);

        try {
            const response = await fetch(url, { signal: controller.signal });
            clearTimeout(timeoutId);

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const data: OverpassResponse = await response.json();

            data.elements.forEach((poi) => {
                let icon: Icon | undefined;
                let layer: LayerGroup | undefined;

                switch (poi.tags.amenity) {
                    case "school":
                    case "university":
                        icon  = schoolIcon;
                        layer = schoolLayer;
                        break;
                    case "bank":
                        icon  = bankIcon;
                        layer = bankLayer;
                        break;
                    case "hospital":
                        icon  = hospitalIcon;
                        layer = hospitalLayer;
                        break;
                    case "cinema":
                        icon  = cinemaIcon;
                        layer = cinemaLayer;
                        break;
                    case "restaurant":
                        icon  = restaurantIcon;
                        layer = restaurantLayer;
                        break;
                    case "pharmacy":
                        icon  = pharmacyIcon;
                        layer = pharmacyLayer;
                        break;
                    case "bus_station":
                        icon  = busIcon;
                        layer = busStationLayer;
                        break;
                    case "charging_station":
                        icon  = chargeIcon;
                        layer = chargingStationLayer;
                        break;
                    case "parking":
                        icon  = parkingIcon;
                        layer = parkingLayer;
                        break;
                }

                if (icon && layer) {
                    new Marker([poi.lat, poi.lon], { icon })
                        .bindPopup(`<b>${poi.tags.name ?? "senza nome"}</b><br>Tipo: ${AMENITY_TRANSLATIONS[poi.tags.amenity] ?? poi.tags.amenity}`)
                        .addTo(layer);

                    amenityCount[poi.tags.amenity] = (amenityCount[poi.tags.amenity] ?? 0) + 1;
                }
            });

            schoolLayer.addTo(mapserv);
            bankLayer.addTo(mapserv);
            hospitalLayer.addTo(mapserv);
            cinemaLayer.addTo(mapserv);
            restaurantLayer.addTo(mapserv);
            pharmacyLayer.addTo(mapserv);
            busStationLayer.addTo(mapserv);
            chargingStationLayer.addTo(mapserv);
            parkingLayer.addTo(mapserv);

            generaReport();

        } catch (error: unknown) {
            clearTimeout(timeoutId);
            if (error instanceof Error && error.name === "AbortError") {
                console.warn("Timeout caricamento POI da Overpass API (8s)");
            } else {
                console.error("Errore caricamento POI:", error);
            }
            generaReport();
        }
    }

    function generaReport(): void {
        if (Object.keys(amenityCount).length === 0) {
            $(".nopoi").hide();
            const markerFallback = new Marker(latLng(lat, lng)).addTo(mapserv);
            const des = `<b>${($("#Titolo").val() as string ?? "").toUpperCase()}</b><br/>`
                      + `${$("#Indirizzo").val() as string ?? ""}<br/>`
                      + `<b>${$("h2.PrezzoProdotto").html() ?? ""}</b><br/>`
                      + ` Cod. ${$("#LottoId").val() as string ?? ""}`;
            markerFallback.bindPopup(des).openPopup();
            mapserv.setZoom(14);
            return;
        }

        const reportDiv = document.getElementById("reportpoi");
        if (!reportDiv) return;
        reportDiv.innerHTML = "";

        const iconMap: Record<string, Icon> = {
            school:           schoolIcon,
            university:       schoolIcon,
            bank:             bankIcon,
            hospital:         hospitalIcon,
            restaurant:       restaurantIcon,
            pharmacy:         pharmacyIcon,
            bus_station:      busIcon,
            cinema:           cinemaIcon,
            charging_station: chargeIcon,
            parking:          parkingIcon,
        };

        for (const amenity in amenityCount) {
            const icon = iconMap[amenity];
            if (!icon) continue;

            const iconUrl = icon.options.iconUrl as string;
            reportDiv.innerHTML += `
            <div class="poitoggle" id="${amenity}">
              <div class="p-2 border mx-3 mb-1 rounded">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <img src="${iconUrl}" style="width:32px;height:32px;vertical-align:middle" alt="icona ${AMENITY_TRANSLATIONS[amenity] ?? amenity}"/>
                    <p class="p-0 m-0 fw-bold small mx-2">${AMENITY_TRANSLATIONS[amenity] ?? amenity}</p>
                  </div>
                  <p class="p-0 m-0 h5 fw-bold mx-2">${amenityCount[amenity]}</p>
                </div>
              </div>
            </div>`;
        }

        document.querySelectorAll<HTMLElement>(".poitoggle").forEach((toggle) => {
            toggle.addEventListener("click", function () {
                const layer = layers[this.id];
                if (!layer) return;

                const isVisible = mapserv.hasLayer(layer);
                if (isVisible) {
                    mapserv.removeLayer(layer);
                    this.classList.add("deselected");
                } else {
                    mapserv.addLayer(layer);
                    this.classList.remove("deselected");
                }
            });
        });
    }
}
